/**
 * The procedural survey behind the Sonar Mosaic landing mockup: a drone track,
 * a seabed with a pipeline, a wreck and boulders, and the forward-looking
 * multibeam fans that sweep it. Pure and deterministic, so every visitor sees
 * the same mosaic build up, and computed once per page load.
 *
 * Frame coordinates are metres with x to the east and y to the south, the
 * same orientation as the canvas the mosaic is drawn on.
 *
 * Ported from Blueye Cloud (`web/src/pages/landing/mockups/sonar-mosaic-field.ts`
 * at 370414e0). Keep the two in step when the Cloud survey changes.
 */

import { mulberry32 } from "../shared/random";
import { forEachPixelNear, type PixelGrid } from "../shared/grid";
import { clamp01, fractalNoise, latticeValue, smoothstep } from "../shared/noise";
import { orderByReveal } from "../shared/reveal";
import { colourise, type PaletteRGB } from "../shared/palette";

export const FRAME_WIDTH_M = 120;
export const FRAME_HEIGHT_M = 80;
const PX_PER_M = 4;
export const FIELD_WIDTH = FRAME_WIDTH_M * PX_PER_M;
export const FIELD_HEIGHT = FRAME_HEIGHT_M * PX_PER_M;
const GRID: PixelGrid = { width: FIELD_WIDTH, height: FIELD_HEIGHT, pxPerM: PX_PER_M };

/** Fan footprint on the seabed — a forward-looking head tilted down. */
export const FAN_RANGE_M = 22;
const FAN_MIN_RANGE_M = 1.5;
export const FAN_HALF_APERTURE_RAD = (65 * Math.PI) / 180;
const SAMPLE_SPACING_M = 0.6;

/** The engine's default display gamma. */
const GAMMA = 0.65;

const TRACK_CONTROL_POINTS: readonly (readonly [number, number])[] = [
    [4, 52],
    [22, 47],
    [40, 35],
    [58, 32],
    [74, 40],
    [88, 47],
    [98, 44],
];

const PIPELINE = { x0: 0, y0: 70, x1: FRAME_WIDTH_M, y1: 14, halfWidth: 0.35 };
const WRECK = { x: 70, y: 51, length: 15, beam: 4.5, angle: (28 * Math.PI) / 180 };
const BOULDER_COUNT = 30;

export interface TrackSample {
    x: number;
    y: number;
    /** Unit heading vector, frame orientation. */
    hx: number;
    hy: number;
}

export interface MosaicField {
    samples: readonly TrackSample[];
    /** Final display intensity per pixel, 0–1; NaN where no fan reached. */
    intensity: Float32Array;
    /** Per-pixel alpha, 0–1: thin at the swath edge, solid where fans overlap. */
    alpha: Float32Array;
    /** Covered pixel indices, ordered by the track sample whose fan first reached them. */
    revealOrder: Uint32Array;
    /** `revealOrder` offset where each sample's pixels start; one extra entry closes the last. */
    revealStart: Uint32Array;
}

let cachedField: MosaicField | null = null;

/** The survey, computed on first use and shared by every mount. */
export function getMosaicField(): MosaicField {
    cachedField ??= buildField();
    return cachedField;
}

/** Straight-alpha RGBA for every pixel, coloured through a sonar palette. */
export function colourField(field: MosaicField, palette: PaletteRGB): Uint8ClampedArray {
    return colourise(field.intensity, field.alpha, palette, { gamma: GAMMA });
}

function buildField(): MosaicField {
    const samples = sampleTrack();
    const reflectivity = seabedReflectivity(samples);
    const coverage = sweepFans(samples);
    const size = FIELD_WIDTH * FIELD_HEIGHT;
    const intensity = new Float32Array(size).fill(Number.NaN);
    const alpha = new Float32Array(size);
    for (let i = 0; i < size; i++) {
        const count = coverage.count[i]!;
        if (count === 0) {
            continue;
        }
        const meanGain = coverage.gain[i]! / count;
        const density = 0.7 + 0.3 * Math.min(1, count / 14);
        intensity[i] = clamp01(reflectivity[i]! * meanGain * density);
        alpha[i] = Math.min(1, 0.3 + count * 0.12);
    }
    const { revealOrder, revealStart } = orderByReveal(coverage.first, samples.length);
    return { samples, intensity, alpha, revealOrder, revealStart };
}

/** A Catmull-Rom curve through the control points, resampled at even spacing. */
function sampleTrack(): TrackSample[] {
    const dense: [number, number][] = [];
    const points = TRACK_CONTROL_POINTS;
    for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[Math.max(0, i - 1)]!;
        const p1 = points[i]!;
        const p2 = points[i + 1]!;
        const p3 = points[Math.min(points.length - 1, i + 2)]!;
        for (let step = 0; step < 40; step++) {
            const t = step / 40;
            dense.push([
                catmullRom(p0[0], p1[0], p2[0], p3[0], t),
                catmullRom(p0[1], p1[1], p2[1], p3[1], t),
            ]);
        }
    }
    const last = points[points.length - 1]!;
    dense.push([last[0], last[1]]);

    const even: [number, number][] = [dense[0]!];
    let carried = 0;
    for (let i = 1; i < dense.length; i++) {
        const [ax, ay] = dense[i - 1]!;
        const [bx, by] = dense[i]!;
        const length = Math.hypot(bx - ax, by - ay);
        let along = SAMPLE_SPACING_M - carried;
        while (along <= length) {
            const t = along / length;
            even.push([ax + (bx - ax) * t, ay + (by - ay) * t]);
            along += SAMPLE_SPACING_M;
        }
        carried = length - (along - SAMPLE_SPACING_M);
    }

    return even.map(([x, y], i) => {
        const [nx, ny] = even[Math.min(even.length - 1, i + 1)]!;
        const [px, py] = even[Math.max(0, i - 1)]!;
        const length = Math.hypot(nx - px, ny - py) || 1;
        return { x, y, hx: (nx - px) / length, hy: (ny - py) / length };
    });
}

function catmullRom(p0: number, p1: number, p2: number, p3: number, t: number): number {
    const t2 = t * t;
    const t3 = t2 * t;
    return (
        0.5 *
        (2 * p1 +
            (-p0 + p2) * t +
            (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
            (-p0 + 3 * p1 - 3 * p2 + p3) * t3)
    );
}

/** Acoustic backscatter of the seabed, 0–1, before the fans see it. */
function seabedReflectivity(samples: readonly TrackSample[]): Float32Array {
    const field = new Float32Array(FIELD_WIDTH * FIELD_HEIGHT);
    const trackY = trackYByColumn(samples);
    const dx = PIPELINE.x1 - PIPELINE.x0;
    const dy = PIPELINE.y1 - PIPELINE.y0;
    const pipeLength = Math.hypot(dx, dy);

    for (let py = 0; py < FIELD_HEIGHT; py++) {
        for (let px = 0; px < FIELD_WIDTH; px++) {
            const x = (px + 0.5) / PX_PER_M;
            const y = (py + 0.5) / PX_PER_M;
            const sediment = fractalNoise(x / 14, y / 14, 7, 4);
            const rock = smoothstep(0.56, 0.66, fractalNoise(x / 22 + 3, y / 22, 13, 3));
            const mud = (1 - rock) * smoothstep(0.55, 0.7, fractalNoise(x / 28 + 5, y / 28, 11, 2));
            const sand = (1 - rock) * (1 - mud);
            let value = 0.12 + 0.2 * sediment;
            if (sand > 0) {
                // Ripples only where the current has worked the sand into fields of them.
                const rippled = smoothstep(0.5, 0.68, fractalNoise(x / 18 + 9, y / 18, 23, 2));
                const phase = (x * 0.82 + y * 0.57) * ((2 * Math.PI) / 1.7) + sediment * 6;
                value += 0.05 * sand * rippled * Math.sin(phase);
            }
            if (rock > 0) {
                value += rock * (0.1 + 0.22 * fractalNoise(x * 0.5, y * 0.5, 17, 2));
            }
            value *= 1 - 0.45 * mud;

            // The pipeline returns strongly and hides the seabed on its far side.
            const signed = ((x - PIPELINE.x0) * dy - (y - PIPELINE.y0) * dx) / pipeLength;
            if (Math.abs(signed) < 2.5) {
                const trackSigned =
                    ((x - PIPELINE.x0) * dy - (trackY[px]! - PIPELINE.y0) * dx) / pipeLength;
                const exposure = smoothstep(0.25, 0.6, fractalNoise(x / 9 + 30, y / 9, 5, 2));
                value = Math.max(
                    value,
                    0.95 * exposure * Math.exp(-((signed / PIPELINE.halfWidth) ** 2)),
                );
                const behind = Math.sign(signed) !== Math.sign(trackSigned);
                const depth = Math.abs(signed) - PIPELINE.halfWidth;
                if (behind && depth > 0 && depth < 1.6) {
                    value *= 1 - 0.7 * exposure * (1 - depth / 1.6);
                }
            }
            field[py * FIELD_WIDTH + px] = value;
        }
    }

    drawWreck(field, samples);
    drawBoulders(field, samples);

    for (let i = 0; i < field.length; i++) {
        const speckle = 0.66 + 0.68 * latticeValue(i % FIELD_WIDTH, Math.floor(i / FIELD_WIDTH), 3);
        field[i] = clamp01(field[i]! * speckle);
    }
    return field;
}

/** The track's northing at each pixel column, for which side of a feature the sonar sat on. */
function trackYByColumn(samples: readonly TrackSample[]): Float32Array {
    const ys = new Float32Array(FIELD_WIDTH);
    let cursor = 0;
    for (let px = 0; px < FIELD_WIDTH; px++) {
        const x = (px + 0.5) / PX_PER_M;
        while (cursor < samples.length - 1 && samples[cursor + 1]!.x < x) {
            cursor++;
        }
        ys[px] = samples[cursor]!.y;
    }
    return ys;
}

function shadowDirection(
    samples: readonly TrackSample[],
    fx: number,
    fy: number,
): [number, number] {
    let nearest = samples[0]!;
    let best = Infinity;
    for (let i = 0; i < samples.length; i++) {
        const sample = samples[i]!;
        const distance = (sample.x - fx) ** 2 + (sample.y - fy) ** 2;
        if (distance < best) {
            best = distance;
            nearest = sample;
        }
    }
    const length = Math.sqrt(best) || 1;
    // Seen partly from the side and partly from behind as the drone passes.
    const sx = (fx - nearest.x) / length + nearest.hx * 0.6;
    const sy = (fy - nearest.y) / length + nearest.hy * 0.6;
    const norm = Math.hypot(sx, sy) || 1;
    return [sx / norm, sy / norm];
}

function drawWreck(field: Float32Array, samples: readonly TrackSample[]): void {
    const [sx, sy] = shadowDirection(samples, WRECK.x, WRECK.y);
    const cos = Math.cos(WRECK.angle);
    const sin = Math.sin(WRECK.angle);
    const halfLength = WRECK.length / 2;
    const halfBeam = WRECK.beam / 2;
    const toLocal = (x: number, y: number): [number, number] => {
        const rx = x - WRECK.x;
        const ry = y - WRECK.y;
        return [rx * cos + ry * sin, -rx * sin + ry * cos];
    };
    // Positive when inside the hull, in metres from its outline; the bow tapers to a point.
    const inside = (u: number, v: number): number => {
        const taper = u > halfLength - 4 ? Math.max(0, (halfLength - u) / 4) : 1;
        return Math.min(halfLength - Math.abs(u), halfBeam * taper - Math.abs(v));
    };
    const reach = halfLength + 8;
    forEachPixelNear(GRID, WRECK.x, WRECK.y, reach, (index, x, y) => {
        const [u, v] = toLocal(x, y);
        const depth = inside(u, v);
        if (depth >= 0) {
            const deck = 0.5 + 0.25 * fractalNoise(x * 1.3, y * 1.3, 19, 2);
            const superstructure = Math.abs(u + 2) < 1.6 && Math.abs(v) < 1.1 ? 0.92 : 0;
            field[index] = Math.max(depth < 0.5 ? 0.98 : deck, superstructure);
            return;
        }
        for (const step of [1, 2.2, 3.6, 5, 6.5]) {
            const [su, sv] = toLocal(x - sx * step, y - sy * step);
            if (inside(su, sv) >= 0) {
                field[index] = field[index]! * (0.15 + 0.07 * step);
                return;
            }
        }
    });
}

function drawBoulders(field: Float32Array, samples: readonly TrackSample[]): void {
    const random = mulberry32(42);
    for (let placed = 0; placed < BOULDER_COUNT;) {
        const bx = 4 + random() * (FRAME_WIDTH_M - 8);
        const by = 6 + random() * (FRAME_HEIGHT_M - 12);
        const radius = 0.45 + random() ** 2 * 1.5;
        if (Math.hypot(bx - WRECK.x, by - WRECK.y) < WRECK.length * 0.7) {
            continue;
        }
        placed++;
        const [sx, sy] = shadowDirection(samples, bx, by);
        const shadowLength = radius * 4;
        forEachPixelNear(GRID, bx, by, radius + shadowLength, (index, x, y) => {
            const rx = x - bx;
            const ry = y - by;
            const distance = Math.sqrt(rx * rx + ry * ry);
            if (distance < radius * 1.4) {
                const lit = 0.96 * Math.exp(-2 * (distance / radius) ** 2);
                field[index] = Math.max(field[index]!, lit);
                return;
            }
            const along = rx * sx + ry * sy;
            const across = Math.abs(rx * sy - ry * sx);
            const tail = (along - radius) / shadowLength;
            if (tail > 0 && tail < 1 && across < radius * (1 - 0.4 * tail)) {
                field[index] = field[index]! * (0.12 + 0.7 * tail);
            }
        });
    }
}

/** How many fans reached each pixel, their summed gain, and the first sample to reach it. */
function sweepFans(samples: readonly TrackSample[]) {
    const size = FIELD_WIDTH * FIELD_HEIGHT;
    const count = new Uint16Array(size);
    const gain = new Float32Array(size);
    const first = new Int32Array(size).fill(-1);
    const cosAperture = Math.cos(FAN_HALF_APERTURE_RAD);

    for (let s = 0; s < samples.length; s++) {
        const sample = samples[s]!;
        const [west, east, north, south] = fanBounds(sample);
        const x0 = Math.max(0, Math.floor(west * PX_PER_M));
        const x1 = Math.min(FIELD_WIDTH - 1, Math.ceil(east * PX_PER_M));
        const y0 = Math.max(0, Math.floor(north * PX_PER_M));
        const y1 = Math.min(FIELD_HEIGHT - 1, Math.ceil(south * PX_PER_M));
        for (let py = y0; py <= y1; py++) {
            const ry = (py + 0.5) / PX_PER_M - sample.y;
            for (let px = x0; px <= x1; px++) {
                const rx = (px + 0.5) / PX_PER_M - sample.x;
                const range = Math.sqrt(rx * rx + ry * ry);
                if (range < FAN_MIN_RANGE_M || range > FAN_RANGE_M) {
                    continue;
                }
                if ((rx * sample.hx + ry * sample.hy) / range < cosAperture) {
                    continue;
                }
                // Weak near the boresight floor and fading toward the end of the range.
                const fade = 1 - 0.4 * (range / FAN_RANGE_M) ** 2;
                const index = py * FIELD_WIDTH + px;
                count[index] = count[index]! + 1;
                gain[index] = gain[index]! + smoothstep(FAN_MIN_RANGE_M, 4.5, range) * fade;
                if (first[index] === -1) {
                    first[index] = s;
                }
            }
        }
    }
    return { count, gain, first };
}

/** West, east, north and south edges of one fan's footprint, in frame metres. */
function fanBounds(sample: TrackSample): [number, number, number, number] {
    const heading = Math.atan2(sample.hy, sample.hx);
    let west = 0;
    let east = 0;
    let north = 0;
    let south = 0;
    const include = (angle: number) => {
        const x = FAN_RANGE_M * Math.cos(angle);
        const y = FAN_RANGE_M * Math.sin(angle);
        west = Math.min(west, x);
        east = Math.max(east, x);
        north = Math.min(north, y);
        south = Math.max(south, y);
    };
    include(heading - FAN_HALF_APERTURE_RAD);
    include(heading + FAN_HALF_APERTURE_RAD);
    // The arc bulges past its ends wherever it crosses a compass axis.
    for (const axis of [0, Math.PI / 2, Math.PI, -Math.PI / 2]) {
        const offset = Math.atan2(Math.sin(axis - heading), Math.cos(axis - heading));
        if (Math.abs(offset) <= FAN_HALF_APERTURE_RAD) {
            include(axis);
        }
    }
    return [sample.x + west, sample.x + east, sample.y + north, sample.y + south];
}
